import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, onAuthStateChanged, signOut, updatePassword } from 'firebase/auth';
import { collection, query, onSnapshot, orderBy, doc, updateDoc, runTransaction, where, getDoc } from 'firebase/firestore';

const Panel = ({ db, auth, onLogout }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loginData, setLoginData] = useState({ email: '', password: '' });
    const [loginError, setLoginError] = useState('');
    const [lections, setLections] = useState([]);
    const [userReservations, setUserReservations] = useState([]);
    const [activeTab, setActiveTab] = useState('lections');
    const [needsPasswordChange, setNeedsPasswordChange] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');

    // Auth state listener
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setUser(user && !user.isAnonymous ? user : null);
            setLoading(false);
            
            if (user && !user.isAnonymous) {
                // Check if user needs to change password
                try {
                    const userDoc = await getDoc(doc(db, 'users', user.uid));
                    if (userDoc.exists()) {
                        setNeedsPasswordChange(userDoc.data().needsPasswordChange || false);
                    }
                } catch (error) {
                    console.error('Error checking user data:', error);
                }
            }
        });
        return () => unsubscribe();
    }, [auth, db]);

    // Load lections when user is authenticated
    useEffect(() => {
        if (user && !needsPasswordChange) {
            // Load lections
            const lectionsQuery = query(collection(db, 'lections'), orderBy('date', 'asc'));
            const unsubscribeLections = onSnapshot(lectionsQuery, (querySnapshot) => {
                const lectionsList = [];
                querySnapshot.forEach((doc) => {
                    lectionsList.push({ id: doc.id, ...doc.data() });
                });
                setLections(lectionsList);

            });

            // Load user's reservations
            const reservationsQuery = query(
                collection(db, 'lectionReservations'),
                where('userId', '==', user.uid)
            );
            const unsubscribeReservations = onSnapshot(reservationsQuery, (querySnapshot) => {
                const reservationsList = [];
                querySnapshot.forEach((doc) => {
                    reservationsList.push({ id: doc.id, ...doc.data() });
                });
                // Sort manually by createdAt to avoid Firestore index issues
                reservationsList.sort((a, b) => {
                    const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
                    const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
                    return dateB - dateA; // Newest first
                });
                setUserReservations(reservationsList);
                console.log('User reservations loaded:', reservationsList); // Debug log
            });

            return () => {
                unsubscribeLections();
                unsubscribeReservations();
            };
        }
    }, [user, db, needsPasswordChange]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        
        try {
            await signInWithEmailAndPassword(auth, loginData.email.trim(), loginData.password);
        } catch (error) {
            setLoginError('Neplatné přihlašovací údaje');
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            onLogout();
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setPasswordError('');

        if (newPassword !== confirmPassword) {
            setPasswordError('Hesla se neshodují');
            return;
        }

        if (newPassword.length < 6) {
            setPasswordError('Heslo musí mít alespoň 6 znaků');
            return;
        }

        try {
            await updatePassword(user, newPassword);
            
            // Update user document to mark password as changed
            await updateDoc(doc(db, 'users', user.uid), {
                needsPasswordChange: false,
                passwordChangedAt: new Date()
            });

            setNeedsPasswordChange(false);
            setNewPassword('');
            setConfirmPassword('');
            alert('Heslo bylo úspěšně změněno!');
        } catch (error) {
            console.error('Error updating password:', error);
            setPasswordError('Chyba při změně hesla: ' + error.message);
        }
    };

    const handleLectionReservation = async (lectionId, lectionName) => {
        if (!window.confirm(`Chcete se přihlásit na lekci „${lectionName}“?`)) return;
        try {
            const lessonRef = doc(db, 'lections', lectionId);
            const reservationRef = doc(db, 'lectionReservations', `${lectionId}_${user.uid}`);
            await runTransaction(db, async transaction => {
                const snapshot = await transaction.get(lessonRef);
                if (!snapshot.exists()) throw new Error('Lekce už není dostupná.');
                const lesson = snapshot.data();
                const map = lesson.attendeesMap || {};
                const legacy = lesson.attendees || [];
                if (map[user.uid] || legacy.some(item => item.userId === user.uid)) throw new Error('Na tuto lekci jste již přihlášeni.');
                if (lesson.archived || new Date(`${lesson.date}T${lesson.time || '00:00'}`) < new Date()) throw new Error('Na tuto lekci se již nelze přihlásit.');
                if (Object.keys(map).length + legacy.length >= 12) throw new Error('Lekce je plná.');
                transaction.update(lessonRef, { attendeesMap: { ...map, [user.uid]: { userId: user.uid, email: user.email, joinedAt: new Date() } } });
                transaction.set(reservationRef, { userId: user.uid, userEmail: user.email, lectionId, lectionName, status: 'confirmed', createdAt: new Date() });
            });
            alert('Úspěšně jste se přihlásili na lekci.');
        } catch (error) { alert(error.message || 'Přihlášení se nepodařilo. Zkuste to znovu.'); }
    };

    const cancelReservation = async (reservationId, lectionId) => {
        if (!window.confirm('Opravdu chcete zrušit tuto rezervaci?')) return;
        try {
            const lessonRef = doc(db, 'lections', lectionId);
            await runTransaction(db, async transaction => {
                const snapshot = await transaction.get(lessonRef);
                if (snapshot.exists()) {
                    const lesson = snapshot.data();
                    const map = { ...(lesson.attendeesMap || {}) };
                    delete map[user.uid];
                    transaction.update(lessonRef, { attendeesMap: map, attendees: (lesson.attendees || []).filter(item => item.userId !== user.uid) });
                }
                transaction.delete(doc(db, 'lectionReservations', reservationId));
            });
            alert('Rezervace byla zrušena.');
        } catch { alert('Zrušení se nepodařilo. Zkuste to znovu nebo kontaktujte lektora.'); }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('cs-CZ', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatTime = (timeString) => {
        return timeString;
    };

    const isUserRegistered = (lection) => {
        // Check both reservation records and attendees array
        const hasReservation = userReservations.some(reservation => 
            reservation.lectionId === lection.id && reservation.status === 'confirmed'
        );
        
        const isInAttendees = !!lection.attendeesMap?.[user.uid] || lection.attendees?.some(attendee => attendee.userId === user.uid);
        
        return hasReservation || isInAttendees;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-white text-xl">Načítání...</div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="bg-gray-800 p-8 rounded-xl shadow-2xl w-full max-w-md">
                    <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-white mb-2">Moje praxe</h1>
                        <p className="text-gray-400">Přihlášení pro klienty</p>
                    </div>

                    <form onSubmit={handleLogin} className="space-y-6">
                        <div>
                            <label htmlFor="client-email" className="block text-white text-sm font-medium mb-2">E-mail</label>
                            <input
                                type="email" id="client-email" autoComplete="username"
                                value={loginData.email}
                                onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                required
                            />
                        </div>

                        <div>
                            <label htmlFor="client-password" className="block text-white text-sm font-medium mb-2">Heslo</label>
                            <input
                                type="password" id="client-password" autoComplete="current-password"
                                value={loginData.password}
                                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                required
                            />
                        </div>

                        {loginError && (
                            <div role="alert" className="text-red-400 text-sm text-center">
                                {loginError}
                            </div>
                        )}

                        <button
                            type="submit"
                            className="w-full bg-yellow-600 hover:bg-yellow-700 text-black font-bold py-3 px-4 rounded-lg transition duration-300"
                        >
                            Přihlásit se
                        </button>
                    </form>

                    <div className="mt-6 text-center">
                        <a 
                            href="/" 
                            className="text-yellow-500 hover:text-yellow-400 text-sm transition duration-300"
                        >
                            ← Zpět na hlavní stránku
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    // Password change modal
    if (needsPasswordChange) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="bg-gray-800 p-8 rounded-xl shadow-2xl w-full max-w-md">
                    <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-white mb-2">Změna hesla</h1>
                        <p className="text-gray-400">Pro pokračování si musíte změnit heslo</p>
                    </div>

                    <form onSubmit={handlePasswordChange} className="space-y-6">
                        <div>
                            <label className="block text-white text-sm font-medium mb-2">
                                Nové heslo
                            </label>
                            <input
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                required
                                minLength={6}
                            />
                        </div>

                        <div>
                            <label className="block text-white text-sm font-medium mb-2">
                                Potvrdit heslo
                            </label>
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 transition duration-300"
                                required
                                minLength={6}
                            />
                        </div>

                        {passwordError && (
                            <div role="alert" className="text-red-400 text-sm text-center">
                                {passwordError}
                            </div>
                        )}

                        <button
                            type="submit"
                            className="w-full bg-yellow-600 hover:bg-yellow-700 text-black font-bold py-3 px-4 rounded-lg transition duration-300"
                        >
                            Změnit heslo
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white">
            {/* Header */}
            <header className="bg-gray-800 shadow-lg">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap gap-4 justify-between items-center py-4">
                        <div className="flex flex-wrap gap-2 items-center">
                            <h1 className="text-2xl font-bold text-yellow-500">Moje praxe</h1>
                            <span className="text-gray-400 break-all">
                                Přihlášen jako: {user.email}
                            </span>
                        </div>
                        <div className="flex items-center space-x-4">
                            <a 
                                href="/" 
                                className="text-gray-300 hover:text-white transition duration-300"
                            >
                                Hlavní stránka
                            </a>
                            <button
                                onClick={handleLogout}
                                className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg transition duration-300"
                            >
                                Odhlásit se
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Navigation Tabs */}
            <nav className="bg-gray-800 border-t border-gray-700">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex space-x-8">
                        <button
                            onClick={() => setActiveTab('lections')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm transition duration-300 ${
                                activeTab === 'lections' 
                                    ? 'border-yellow-500 text-yellow-500' 
                                    : 'border-transparent text-gray-400 hover:text-gray-300'
                            }`}
                        >
                            Dostupné lekce ({lections.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('myReservations')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm transition duration-300 ${
                                activeTab === 'myReservations' 
                                    ? 'border-yellow-500 text-yellow-500' 
                                    : 'border-transparent text-gray-400 hover:text-gray-300'
                            }`}
                        >
                            Moje rezervace ({userReservations.length})
                        </button>
                    </div>
                </div>
            </nav>

            {/* Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {activeTab === 'lections' && (
                    <div>
                        <h2 className="text-3xl font-bold mb-8">Dostupné lekce</h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {lections.map((lection) => {
                                const isRegistered = isUserRegistered(lection);
                                const isPastDate = new Date(lection.date) < new Date();
                                
                                return (
                                    <div key={lection.id} className="bg-gray-800 rounded-lg overflow-hidden shadow-lg">
                                        <img 
                                            src={lection.image} 
                                            alt={lection.name}
                                            className="w-full h-48 object-cover"
                                        />
                                        <div className="p-6">
                                            <h3 className="text-xl font-bold text-white mb-3">{lection.name}</h3>
                                            <p className="text-gray-300 mb-4">{lection.description}</p>
                                            
                                            <div className="space-y-2 mb-6">
                                                <div className="flex items-center text-gray-400">
                                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                    {formatDate(lection.date)}
                                                </div>
                                                <div className="flex items-center text-gray-400">
                                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    {formatTime(lection.time)}
                                                </div>
                                                <div className="flex items-center text-gray-400">
                                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                    </svg>
                                                    {(Object.keys(lection.attendeesMap || {}).length + (lection.attendees?.length || 0))} účastníků
                                                </div>
                                            </div>

                                            {isPastDate ? (
                                                <div className="bg-gray-600 text-gray-300 text-center py-2 px-4 rounded-lg">
                                                    Lekce již proběhla
                                                </div>
                                            ) : isRegistered ? (
                                                <div className="bg-green-600 text-white text-center py-2 px-4 rounded-lg">
                                                    ✓ Přihlášeno
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => handleLectionReservation(lection.id, lection.name)}
                                                    className="w-full bg-yellow-600 hover:bg-yellow-700 text-black font-bold py-2 px-4 rounded-lg transition duration-300"
                                                >
                                                    Přihlásit se
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        {lections.length === 0 && (
                            <div className="text-center py-16">
                                <div className="bg-gray-800 rounded-xl p-8">
                                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                    <h3 className="text-xl font-semibold text-gray-300 mb-2">Žádné lekce k dispozici</h3>
                                    <p className="text-gray-400">Momentálně nejsou naplánované žádné lekce.</p>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'myReservations' && (
                    <div>
                        <h2 className="text-3xl font-bold mb-8">Moje rezervace</h2>
                        
                        <div className="space-y-4">
                            {userReservations.map((reservation) => {
                                const lection = lections.find(l => l.id === reservation.lectionId);
                                return (
                                    <div key={reservation.id} className="bg-gray-800 rounded-lg p-6">
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <h3 className="text-xl font-bold text-white mb-2">{reservation.lectionName}</h3>
                                                {lection && (
                                                    <div className="space-y-1 text-sm text-gray-400">
                                                        <div className="flex flex-wrap gap-2 items-center">
                                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                            </svg>
                                                            {formatDate(lection.date)}
                                                        </div>
                                                        <div className="flex flex-wrap gap-2 items-center">
                                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            {formatTime(lection.time)}
                                                        </div>
                                                    </div>
                                                )}
                                                <p className="text-xs text-gray-500 mt-2">
                                                    Přihlášeno: {(() => {
                                                        const date = reservation.createdAt?.toDate?.() || new Date(reservation.createdAt) || new Date();
                                                        return date.toLocaleDateString('cs-CZ');
                                                    })()}
                                                </p>
                                            </div>
                                            
                                            <div className="flex items-center space-x-4">
                                                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                                    reservation.status === 'confirmed' ? 'bg-green-500 text-white' :
                                                    'bg-yellow-500 text-black'
                                                }`}>
                                                    {reservation.status === 'confirmed' ? 'Potvrzeno' : 'Čeká'}
                                                </span>
                                                
                                                {lection && new Date(lection.date) > new Date() && (
                                                    <button
                                                        onClick={() => cancelReservation(reservation.id, reservation.lectionId)}
                                                        className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium py-1 px-3 rounded transition duration-300"
                                                    >
                                                        Zrušit
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        {userReservations.length === 0 && (
                            <div className="text-center py-16">
                                <div className="bg-gray-800 rounded-xl p-8">
                                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                    </svg>
                                    <h3 className="text-xl font-semibold text-gray-300 mb-2">Žádné rezervace</h3>
                                    <p className="text-gray-400">Ještě jste se nepřihlásili na žádnou lekci.</p>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
};

export default Panel;
